/**
 * LoginTooltip Component
 * ------------------------------------------------------------------------------
 * A reusable component for handling login tooltip functionality.
 */

const selectors = {
  loginTooltip: '#loginTooltip',
  loginTriggers: '[data-login-trigger]',
  loginForm: '.login-tooltip__form',
  loginSubmitButton: '.login-tooltip__submit',
  loginInputs: '.login-tooltip__input',
  accountWrapper: '.header__account-wrapper',
};

class LoginTooltip {
  constructor() {
    this.loginTooltip = null;
    this.loginTriggers = [];
    this.loginForm = null;
    this.loginSubmitButton = null;
    this.accountWrapper = null;
    this.isOpen = false;

    this.init();
  }

  init() {
    this.loginTooltip = document.querySelector(selectors.loginTooltip);
    this.loginTriggers = document.querySelectorAll(selectors.loginTriggers);
    this.loginForm = this.loginTooltip?.querySelector(selectors.loginForm);
    this.loginSubmitButton = this.loginTooltip?.querySelector(selectors.loginSubmitButton);
    this.accountWrapper = document.querySelector(selectors.accountWrapper);

    if (!this.loginTooltip) return;

    this.bindEvents();
  }

  bindEvents() {
    // Toggle tooltip triggers
    this.loginTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggle();
      });
    });

    // Allow register link to work normally
    const registerLink = this.loginTooltip?.querySelector('.login-tooltip__register-link');
    if (registerLink) {
      registerLink.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent tooltip from closing
        // Let the link navigate normally - don't prevent default
        this.close(); // Close tooltip before navigation
      });
    }

    // Close on click outside
    document.addEventListener('click', (e) => {
      if (this.isOpen && !this.accountWrapper.contains(e.target)) {
        this.close();
      }
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Form submission - only validate, let form submit naturally
    if (this.loginForm) {
      this.loginForm.addEventListener('submit', (e) => {
        if (!this.validateForm()) {
          e.preventDefault(); // Only prevent if validation fails
        } else {
          // Form is valid, let it submit naturally
          this.trackEvent('login_tooltip_submit_attempt');
        }
      });
    }

    // Input validation
    const inputs = this.loginTooltip.querySelectorAll(selectors.loginInputs);
    inputs.forEach(input => {
      input.addEventListener('input', () => {
        this.clearInputError(input);
        this.updateSubmitButtonState();
      });
    });

    // Close on scroll (optional - for better UX)
    window.addEventListener('scroll', () => {
      if (this.isOpen) {
        this.close();
      }
    });
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (!this.loginTooltip || this.isOpen) return;

    // Close any other open tooltips/dropdowns first
    this.closeOtherDropdowns();

    // Set return URL to current page
    const returnInput = this.loginTooltip.querySelector('input[name="return_to"]');
    if (returnInput && typeof window !== 'undefined' && window.location) {
      returnInput.value = window.location.pathname + window.location.search;
    }

    this.loginTooltip.classList.add('active');
    this.loginTooltip.setAttribute('aria-hidden', 'false');
    this.isOpen = true;

    // Initialize submit button state
    this.updateSubmitButtonState();

    // Focus the first input
    const firstInput = this.loginTooltip.querySelector(selectors.loginInputs);
    if (firstInput) {
      setTimeout(() => firstInput.focus(), 100);
    }

    // Track tooltip opening
    this.trackEvent('login_tooltip_opened');
  }

  close() {
    if (!this.loginTooltip || !this.isOpen) return;

    this.loginTooltip.classList.remove('active');
    this.loginTooltip.setAttribute('aria-hidden', 'true');
    this.isOpen = false;

    // Clear any error states
    this.clearFormErrors();
  }

  closeOtherDropdowns() {
    // Close any other open dropdowns/tooltips that might conflict
    const openDropdowns = document.querySelectorAll('.currency-popup.active, .search-popup.active');
    openDropdowns.forEach(dropdown => {
      dropdown.classList.remove('active');
    });
  }

  validateForm() {
    const email = this.loginTooltip.querySelector('#loginTooltipEmail');
    const password = this.loginTooltip.querySelector('#loginTooltipPassword');
    let isValid = true;

    // Validate email
    if (!email.value.trim()) {
      this.showInputError(email, 'Email is required');
      isValid = false;
    } else if (!this.isValidEmail(email.value)) {
      this.showInputError(email, 'Please enter a valid email');
      isValid = false;
    }

    // Validate password
    if (!password.value.trim()) {
      this.showInputError(password, 'Password is required');
      isValid = false;
    }

    this.updateSubmitButtonState();
    return isValid;
  }

  updateSubmitButtonState() {
    if (!this.loginSubmitButton) return;

    const email = this.loginTooltip.querySelector('#loginTooltipEmail');
    const password = this.loginTooltip.querySelector('#loginTooltipPassword');

    // Check if both fields have values and no visible errors
    const emailValid = email.value.trim() && this.isValidEmail(email.value.trim()) && !email.classList.contains('error');
    const passwordValid = password.value.trim() && !password.classList.contains('error');
    const hasFormError = this.loginTooltip.querySelector('.login-tooltip__form-error');

    if (emailValid && passwordValid && !hasFormError) {
      this.loginSubmitButton.disabled = false;
    } else {
      this.loginSubmitButton.disabled = true;
    }
  }

  validateInput(input) {
    const value = input.value.trim();

    if (input.type === 'email') {
      if (!value) {
        this.showInputError(input, 'Email is required');
        return false;
      } else if (!this.isValidEmail(value)) {
        this.showInputError(input, 'Please enter a valid email');
        return false;
      }
    } else if (input.type === 'password') {
      if (!value) {
        this.showInputError(input, 'Password is required');
        return false;
      }
    }

    this.clearInputError(input);
    return true;
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  showInputError(input, message) {
    this.clearInputError(input);

    input.classList.add('error');
    const errorEl = document.createElement('div');
    errorEl.className = 'login-tooltip__field-error';
    errorEl.textContent = message;

    input.parentElement.appendChild(errorEl);
  }

  clearInputError(input) {
    input.classList.remove('error');
    const errorEl = input.parentElement.querySelector('.login-tooltip__field-error');
    if (errorEl) {
      errorEl.remove();
    }
  }

  clearFormErrors() {
    const inputs = this.loginTooltip.querySelectorAll(selectors.loginInputs);
    inputs.forEach(input => this.clearInputError(input));

    const formError = this.loginTooltip.querySelector('.login-tooltip__form-error');
    if (formError) {
      formError.remove();
    }

    this.updateSubmitButtonState();
  }

  showFormError(message) {
    this.clearFormErrors();

    const errorEl = document.createElement('div');
    errorEl.className = 'login-tooltip__form-error';
    errorEl.textContent = message;

    this.loginForm.insertBefore(errorEl, this.loginSubmitButton);
    this.updateSubmitButtonState();
  }

  trackEvent(eventName, data = {}) {
    // Track events for analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, data);
    }

    if (typeof dataLayer !== 'undefined') {
      dataLayer.push({
        event: eventName,
        ...data
      });
    }
  }

  openTooltip() {
    this.open();
  }

  closeTooltip() {
    this.close();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const header = document.querySelector('header');
  if (header) {
    new LoginTooltip(header);
  }

  initAttentionTooltip();
});

function initAttentionTooltip() {
  const attentionTooltip = document.getElementById('attentionTooltip');
  const loginTrigger = document.querySelector('[data-login-trigger]');
  const isShowed = localStorage.getItem("promptShowed");

  if (!attentionTooltip || !loginTrigger) return;

  setTimeout(() => {
    if (attentionTooltip && !attentionTooltip.classList.contains('hidden') && !isShowed) {
      localStorage.setItem("promptShowed", true);
      attentionTooltip.classList.add('show');
    }
  }, 3000);

  attentionTooltip.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    hideAttentionTooltip();
    loginTrigger.click();
  });

  loginTrigger.addEventListener('click', () => {
    hideAttentionTooltip();
  });

  const closeBtn = attentionTooltip.querySelector('.attention-tooltip__close');
  if (closeBtn) {
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      hideAttentionTooltip();
    });
  }

  setTimeout(() => {
    hideAttentionTooltip();
  }, 12000);

  function hideAttentionTooltip() {
    if (attentionTooltip) {
      attentionTooltip.classList.remove('show');
      attentionTooltip.classList.add('hidden');
    }
  }
}